import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import AdminSectionCard, {
  AdminButton,
  AdminEmptyState,
  AdminField,
  AdminScrollTable,
  AdminStatusBadge,
} from "./AdminSectionCard";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL ||
  (window.location.hostname === "localhost" ? "http://localhost:5000" : "https://api.gubcpc.com");

export default function AdminJulyContestPage() {
  const token = useSelector((state) => state.adminAuth?.accessToken);

  // Filters & Pagination state
  const [search, setSearch] = useState("");
  const [sortBy, setSortBy] = useState("createdAt");
  const [sortOrder, setSortOrder] = useState("DESC");
  const [page, setPage] = useState(1);
  const [limit] = useState(20);

  // Data state
  const [registrations, setRegistrations] = useState([]);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  // Modal / Selected registration state
  const [selectedReg, setSelectedReg] = useState(null);
  const [editStatus, setEditStatus] = useState("");
  const [editRemarks, setEditRemarks] = useState("");
  const [updating, setUpdating] = useState(false);
  const [deletingId, setDeletingId] = useState(null);

  // Statistics state
  const [stats, setStats] = useState({
    total: 0,
    verified: 0,
    pending: 0,
    rejected: 0,
  });

  const fetchWithAuth = async (url, options = {}) => {
    const headers = {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...options.headers,
    };
    const response = await fetch(`${API_BASE_URL}${url}`, {
      ...options,
      headers,
      credentials: "include",
    });
    const data = await response.json();
    if (!response.ok) {
      throw new Error(data.message || "Request failed");
    }
    return data;
  };

  const loadData = async () => {
    setLoading(true);
    setError("");
    try {
      const params = new URLSearchParams({
        search,
        sortBy,
        sortOrder,
        page,
        limit,
      });

      const res = await fetchWithAuth(`/admin/july-contest/registrations?${params.toString()}`);
      setRegistrations(res.registrations || []);
      setTotal(res.total || 0);
      setTotalPages(res.totalPages || 1);

      // Load all registrations for stats computation (or query from backend)
      const allRes = await fetchWithAuth(`/admin/july-contest/registrations?limit=1000`);
      const allRegs = allRes.registrations || [];
      const statsObj = {
        total: allRegs.length,
        verified: allRegs.filter((r) => r.status === "Verified").length,
        pending: allRegs.filter((r) => r.status === "Pending verification").length,
        rejected: allRegs.filter((r) => r.status === "Rejected").length,
      };
      setStats(statsObj);
    } catch (err) {
      setError(err.message || "Failed to load registrations.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (token) {
      loadData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, page, sortBy, sortOrder]);

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    setPage(1);
    loadData();
  };

  const handleUpdateStatus = async (e) => {
    e.preventDefault();
    if (!selectedReg) return;
    setUpdating(true);
    setError("");
    setSuccess("");
    try {
      const res = await fetchWithAuth(`/admin/july-contest/registrations/${selectedReg.id}`, {
        method: "PATCH",
        body: JSON.stringify({
          status: editStatus,
          remarks: editRemarks,
        }),
      });

      setSuccess("Registration updated successfully!");
      // Update local item
      setRegistrations((prev) =>
        prev.map((item) => (item.id === selectedReg.id ? res.registration : item))
      );
      setSelectedReg(res.registration);

      // Reload statistical data
      const allRes = await fetchWithAuth(`/admin/july-contest/registrations?limit=1000`);
      const allRegs = allRes.registrations || [];
      setStats({
        total: allRegs.length,
        verified: allRegs.filter((r) => r.status === "Verified").length,
        pending: allRegs.filter((r) => r.status === "Pending verification").length,
        rejected: allRegs.filter((r) => r.status === "Rejected").length,
      });
    } catch (err) {
      setError(err.message || "Failed to update registration status.");
    } finally {
      setUpdating(false);
    }
  };

  const handleCopyData = async (type) => {
    try {
      // Fetch all registrations
      const allRes = await fetchWithAuth(`/admin/july-contest/registrations?limit=1000`);
      const allRegs = allRes.registrations || [];

      let list = [];
      if (type === "leader-emails") {
        list = allRegs.map((r) => r.leaderEmail);
      } else if (type === "leader-phones") {
        list = allRegs.map((r) => r.leaderPhone);
      } else if (type === "member2-emails") {
        list = allRegs.map((r) => r.secondMemberEmail);
      } else if (type === "member2-phones") {
        list = allRegs.map((r) => r.secondMemberPhone);
      } else if (type === "all-emails") {
        list = allRegs.flatMap((r) => [r.leaderEmail, r.secondMemberEmail]);
      } else if (type === "all-phones") {
        list = allRegs.flatMap((r) => [r.leaderPhone, r.secondMemberPhone]);
      }

      const cleanList = [...new Set(list.map((item) => String(item || "").trim()).filter(Boolean))];
      await navigator.clipboard.writeText(cleanList.join(", "));
      setSuccess(`${cleanList.length} items copied to clipboard!`);
      setTimeout(() => setSuccess(""), 3000);
    } catch (err) {
      setError("Failed to copy data: " + err.message);
    }
  };

  const openDetailsModal = (reg) => {
    setSelectedReg(reg);
    setEditStatus(reg.status);
    setEditRemarks(reg.remarks || "");
    setSuccess("");
    setError("");
  };

  const handleDeleteRegistration = async (reg) => {
    if (!reg) return;

    const confirmed = window.confirm(`Delete team "${reg.teamName}" permanently?`);
    if (!confirmed) return;

    setDeletingId(reg.id);
    setError("");
    setSuccess("");

    try {
      await fetchWithAuth(`/admin/july-contest/registrations/${reg.id}`, {
        method: "DELETE",
      });

      if (selectedReg?.id === reg.id) {
        setSelectedReg(null);
      }

      setSuccess("Registration deleted successfully!");
      const nextTotal = Math.max(0, total - 1);
      const nextTotalPages = Math.max(1, Math.ceil(nextTotal / limit));

      if (page > nextTotalPages) {
        setPage(nextTotalPages);
      } else {
        await loadData();
      }
    } catch (err) {
      setError(err.message || "Failed to delete registration.");
    } finally {
      setDeletingId(null);
    }
  };

  const handleDownloadExcel = async () => {
    try {
      setError("");
      const params = new URLSearchParams({
        search,
        sortBy,
        sortOrder,
      });
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const response = await fetch(`${API_BASE_URL}/admin/july-contest/export?${params.toString()}`, {
        headers,
        credentials: "include",
      });

      if (!response.ok) {
        const errText = await response.text();
        throw new Error(errText || "Export failed");
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `july-contest-registrations-${new Date().toISOString().slice(0, 10)}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setError(err.message || "Failed to download spreadsheet.");
    }
  };

  return (
    <div className="space-y-6">
      {/* Page Header & Statistics Cards */}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <div className="rounded-2xl border border-slate-100 bg-white p-5 shadow-sm">
          <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Total Teams</span>
          <p className="mt-2 text-3xl font-bold text-slate-900">{stats.total}</p>
        </div>
        <div className="rounded-2xl border border-slate-100 bg-white p-5 shadow-sm">
          <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Verified</span>
          <p className="mt-2 text-3xl font-bold text-emerald-600">{stats.verified}</p>
        </div>
        <div className="rounded-2xl border border-slate-100 bg-white p-5 shadow-sm">
          <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Pending Verification</span>
          <p className="mt-2 text-3xl font-bold text-amber-500">{stats.pending}</p>
        </div>
        <div className="rounded-2xl border border-slate-100 bg-white p-5 shadow-sm">
          <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Rejected</span>
          <p className="mt-2 text-3xl font-bold text-red-600">{stats.rejected}</p>
        </div>
      </div>

      {success ? (
        <div className="rounded-xl bg-emerald-50 border border-emerald-200 p-4 text-sm text-emerald-800 font-medium">
          {success}
        </div>
      ) : null}

      {error ? (
        <div className="rounded-xl bg-rose-50 border border-rose-200 p-4 text-sm text-rose-800 font-medium">
          {error}
        </div>
      ) : null}

      {/* Copy Actions Card */}
      <AdminSectionCard title="Quick Copy Options" subtitle="Quickly copy participants contact lists to clipboard">
        <div className="flex flex-wrap gap-3">
          <AdminButton onClick={() => handleCopyData("all-emails")}>Copy All Emails</AdminButton>
          <AdminButton onClick={() => handleCopyData("all-phones")}>Copy All Phone Numbers</AdminButton>
          <AdminButton onClick={() => handleCopyData("leader-emails")} variant="secondary">Copy Leader Emails</AdminButton>
          <AdminButton onClick={() => handleCopyData("leader-phones")} variant="secondary">Copy Leader Phones</AdminButton>
          <AdminButton onClick={() => handleCopyData("member2-emails")} variant="secondary">Copy 2nd Member Emails</AdminButton>
          <AdminButton onClick={() => handleCopyData("member2-phones")} variant="secondary">Copy 2nd Member Phones</AdminButton>
        </div>
      </AdminSectionCard>

      {/* Search & Main Registration List */}
      <AdminSectionCard title="July Memorial Contest Registrations" subtitle="Manage registrant verification statuses and view details">
        <form onSubmit={handleSearchSubmit} className="flex flex-col sm:flex-row gap-3 mb-5">
          <input
            type="text"
            placeholder="Search by Team, Leader name, Email, Student ID, Transaction ID..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="flex-1 rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm text-slate-900 outline-none focus:border-slate-400 focus:ring-4 focus:ring-slate-100"
          />
          <div className="flex gap-2">
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm text-slate-700 outline-none focus:border-slate-400"
            >
              <option value="createdAt">Date Submitted</option>
              <option value="teamName">Team Name</option>
              <option value="leaderName">Leader Name</option>
              <option value="leaderStudentId">Leader ID</option>
              <option value="status">Status</option>
            </select>
            <select
              value={sortOrder}
              onChange={(e) => setSortOrder(e.target.value)}
              className="rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm text-slate-700 outline-none focus:border-slate-400"
            >
              <option value="DESC">Descending</option>
              <option value="ASC">Ascending</option>
            </select>
            <AdminButton type="submit">Search</AdminButton>
            <AdminButton type="button" onClick={handleDownloadExcel} variant="secondary">
              Download Sheet
            </AdminButton>
          </div>
        </form>

        {loading ? (
          <div className="py-12 text-center text-sm font-semibold text-slate-500">Loading registrations...</div>
        ) : registrations.length > 0 ? (
          <div className="space-y-4">
            <AdminScrollTable>
              <table className="w-full min-w-[900px] text-left text-sm">
                <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
                  <tr>
                    <th className="px-4 py-3">Team Name</th>
                    <th className="px-4 py-3">Leader Info</th>
                    <th className="px-4 py-3">Second Member</th>
                    <th className="px-4 py-3">Payment details</th>
                    <th className="px-4 py-3">Status</th>
                    <th className="px-4 py-3 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {registrations.map((row) => (
                    <tr key={row.id} className="bg-white hover:bg-slate-50/70">
                      <td className="px-4 py-4 font-semibold text-slate-800">
                        {row.teamName}
                      </td>
                      <td className="px-4 py-4">
                        <div className="font-semibold text-slate-700">{row.leaderName}</div>
                        <div className="text-xs text-slate-500">ID: {row.leaderStudentId} ({row.leaderDepartment})</div>
                        <div className="text-xs text-slate-400">{row.leaderEmail} | {row.leaderPhone}</div>
                      </td>
                      <td className="px-4 py-4">
                        <div className="font-semibold text-slate-700">{row.secondMemberName}</div>
                        <div className="text-xs text-slate-500">ID: {row.secondMemberStudentId} ({row.secondMemberDepartment})</div>
                        <div className="text-xs text-slate-400">{row.secondMemberEmail} | {row.secondMemberPhone}</div>
                      </td>
                      <td className="px-4 py-4">
                        <div className="font-semibold text-slate-700 uppercase">{row.paymentWay}</div>
                        <div className="text-xs text-slate-500">Sender: {row.paymentNumber}</div>
                        <div className="text-xs text-slate-400">Txn: <span className="font-mono">{row.transactionId}</span></div>
                      </td>
                      <td className="px-4 py-4">
                        <AdminStatusBadge
                          tone={
                            row.status === "Verified"
                              ? "green"
                              : row.status === "Rejected"
                                ? "red"
                                : "amber"
                          }
                        >
                          {row.status}
                        </AdminStatusBadge>
                      </td>
                      <td className="px-4 py-4 text-right">
                        <div className="flex justify-end gap-2">
                          <AdminButton onClick={() => openDetailsModal(row)} className="h-9 px-3 text-xs">
                            Manage
                          </AdminButton>
                          <AdminButton
                            onClick={() => handleDeleteRegistration(row)}
                            className="h-9 px-3 text-xs"
                            variant="danger"
                            disabled={deletingId === row.id}
                          >
                            {deletingId === row.id ? "Deleting..." : "Delete"}
                          </AdminButton>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </AdminScrollTable>

            {/* Pagination */}
            <div className="flex justify-between items-center border-t border-slate-100 pt-4">
              <span className="text-xs text-slate-500">
                Showing page {page} of {totalPages} ({total} total registrations)
              </span>
              <div className="flex gap-2">
                <button
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page === 1}
                  className="rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-semibold text-slate-600 hover:bg-slate-50 disabled:opacity-50"
                >
                  Previous
                </button>
                <button
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages}
                  className="rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-semibold text-slate-600 hover:bg-slate-50 disabled:opacity-50"
                >
                  Next
                </button>
              </div>
            </div>
          </div>
        ) : (
          <AdminEmptyState text="No registrations match the query." />
        )}
      </AdminSectionCard>

      {/* Details & Action Modal */}
      {selectedReg ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 p-4 backdrop-blur-sm">
          <div className="w-full max-w-2xl rounded-2xl bg-white p-6 shadow-xl border border-slate-100 max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center border-b border-slate-100 pb-4">
              <h3 className="text-lg font-bold text-slate-800">
                Team: {selectedReg.teamName}
              </h3>
              <button
                onClick={() => setSelectedReg(null)}
                className="text-slate-400 hover:text-slate-600 transition-colors text-lg"
              >
                ✕
              </button>
            </div>

            <div className="mt-5 space-y-5">
              {/* Leader Info */}
              <div>
                <h4 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-2">Team Leader Information</h4>
                <div className="rounded-xl bg-slate-50 p-4 grid grid-cols-2 gap-4 text-sm text-slate-700">
                  <div>
                    <span className="block text-xs text-slate-400">Name</span>
                    <strong>{selectedReg.leaderName}</strong>
                  </div>
                  <div>
                    <span className="block text-xs text-slate-400">Student ID</span>
                    <strong>{selectedReg.leaderStudentId}</strong>
                  </div>
                  <div>
                    <span className="block text-xs text-slate-400">Email</span>
                    <strong>{selectedReg.leaderEmail}</strong>
                  </div>
                  <div>
                    <span className="block text-xs text-slate-400">Phone</span>
                    <strong>{selectedReg.leaderPhone}</strong>
                  </div>
                  <div className="col-span-2">
                    <span className="block text-xs text-slate-400">Department</span>
                    <strong>{selectedReg.leaderDepartment}</strong>
                  </div>
                </div>
              </div>

              {/* 2nd Member Info */}
              <div>
                <h4 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-2">Second Member Information</h4>
                <div className="rounded-xl bg-slate-50 p-4 grid grid-cols-2 gap-4 text-sm text-slate-700">
                  <div>
                    <span className="block text-xs text-slate-400">Name</span>
                    <strong>{selectedReg.secondMemberName}</strong>
                  </div>
                  <div>
                    <span className="block text-xs text-slate-400">Student ID</span>
                    <strong>{selectedReg.secondMemberStudentId}</strong>
                  </div>
                  <div>
                    <span className="block text-xs text-slate-400">Email</span>
                    <strong>{selectedReg.secondMemberEmail}</strong>
                  </div>
                  <div>
                    <span className="block text-xs text-slate-400">Phone</span>
                    <strong>{selectedReg.secondMemberPhone}</strong>
                  </div>
                  <div className="col-span-2">
                    <span className="block text-xs text-slate-400">Department</span>
                    <strong>{selectedReg.secondMemberDepartment}</strong>
                  </div>
                </div>
              </div>

              {/* Payment Info */}
              <div>
                <h4 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-2">Payment Details</h4>
                <div className="rounded-xl bg-slate-50 p-4 grid grid-cols-3 gap-4 text-sm text-slate-700">
                  <div>
                    <span className="block text-xs text-slate-400">Method</span>
                    <strong className="uppercase">{selectedReg.paymentWay}</strong>
                  </div>
                  <div>
                    <span className="block text-xs text-slate-400">Transaction ID</span>
                    <strong className="font-mono text-xs">{selectedReg.transactionId}</strong>
                  </div>
                  <div>
                    <span className="block text-xs text-slate-400">Sender Number</span>
                    <strong>{selectedReg.paymentNumber}</strong>
                  </div>
                </div>
              </div>

              {/* Update Verification Form */}
              <form onSubmit={handleUpdateStatus} className="border-t border-slate-100 pt-4 space-y-4">
                <h4 className="text-xs font-bold text-slate-400 uppercase tracking-wider">Update Registration Status</h4>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <AdminField label="Status">
                    <select
                      value={editStatus}
                      onChange={(e) => setEditStatus(e.target.value)}
                      className="w-full rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm text-slate-900 outline-none focus:border-slate-400"
                    >
                      <option value="Pending verification">Pending verification</option>
                      <option value="Verified">Verified</option>
                      <option value="Rejected">Rejected</option>
                    </select>
                  </AdminField>

                  <AdminField label="Remarks / Feedback">
                    <textarea
                      placeholder="Add remarks or reasons if rejecting..."
                      value={editRemarks}
                      onChange={(e) => setEditRemarks(e.target.value)}
                      className="w-full rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm text-slate-900 outline-none focus:border-slate-400 h-[80px] resize-none"
                    />
                  </AdminField>
                </div>

                <div className="flex justify-end gap-3">
                  <AdminButton
                    type="button"
                    variant="danger"
                    onClick={() => handleDeleteRegistration(selectedReg)}
                    disabled={updating || deletingId === selectedReg.id}
                  >
                    {deletingId === selectedReg.id ? "Deleting..." : "Delete Team"}
                  </AdminButton>
                  <AdminButton
                    type="button"
                    variant="secondary"
                    onClick={() => setSelectedReg(null)}
                    disabled={updating}
                  >
                    Cancel
                  </AdminButton>
                  <AdminButton type="submit" disabled={updating}>
                    {updating ? "Saving..." : "Save Changes"}
                  </AdminButton>
                </div>
              </form>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
